import * as _ from 'lodash-es';
import type { Range } from 'quill/core';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type Quill from './QuillEditor';
import type { Blot } from './types';
import HOCWithDisplayName from '../utils/HOCWithDisplayName';

const NO_CONTEXT_ERROR = 'Could not find Quill context value.';

interface State {
  ancestorQuill: Quill | null;
  quill: Quill | null;
  selection: {
    formats: Record<string, unknown>;
    range: Range | null;
  };
  editingLink: boolean;
  readOnly: boolean;
  activeBlot: Blot | null;
}

interface Mutations {
  editorMounted(quill: Quill): void;
  editorUnmounted(): void;
  localSelection(selection: {
    formats: Record<string, unknown>;
    range: Range | null;
  }): void;
  editingLinkChange(editing: boolean): void;
  readOnlyChange(readOnly: boolean): void;
  onActiveBlotChange(blot: Blot | null): void;
}

interface ContextValue extends State, Mutations {}

const Context = createContext<ContextValue | null>(null);

export const useQuill = () => {
  const value = useContext(Context);
  if (!value) {
    throw new Error(NO_CONTEXT_ERROR);
  }
  return value;
};

export interface WithQuillProps {
  quillContext: ContextValue;
}

export const withQuill =
  <P extends WithQuillProps>(Component: React.ComponentType<P>) =>
  (props: Omit<P, keyof WithQuillProps>) => (
    <Context.Consumer>
      {(value) => {
        if (!value) {
          throw new Error(NO_CONTEXT_ERROR);
        }
        return <Component {...(props as P)} quillContext={value} />;
      }}
    </Context.Consumer>
  );

const getInitialState = (): State => ({
  ancestorQuill: null,
  quill: null,
  selection: {
    formats: {},
    range: null,
  },
  editingLink: false,
  readOnly: false,
  activeBlot: null,
});

type Action =
  | { type: 'editorMounted'; value: Quill }
  | { type: 'editorUnmounted' }
  | {
      type: 'localSelection';
      value: {
        range: Range | null;
        formats: Record<string, unknown>;
      };
    }
  | { type: 'editingLinkChange'; value: boolean }
  | { type: 'readOnlyChange'; value: boolean }
  | {
      type: 'activeBlotChange';
      value: Blot | null;
    };

const quillReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'editorMounted':
      return { ...state, quill: action.value };
    case 'editorUnmounted':
      return getInitialState();
    case 'localSelection': {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const rangeChanged = state.selection.range !== action.value.range;
      return {
        ...state,
        selection: { ...action.value },
        editingLink: rangeChanged ? false : state.editingLink,
      };
    }
    case 'editingLinkChange':
      return state.editingLink === action.value
        ? state
        : { ...state, editingLink: action.value };
    case 'readOnlyChange':
      return state.readOnly === action.value
        ? state
        : { ...state, readOnly: action.value };
    case 'activeBlotChange':
      return state.activeBlot === action.value
        ? state
        : { ...state, activeBlot: action.value };
    default:
      return state;
  }
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export interface QuillContextProps {
  children: ReactNode;
  initialValues?: Partial<State>;
}

const QuillContext = ({ children, initialValues }: QuillContextProps) => {
  const [state, dispatch] = useReducer(
    quillReducer,
    _.merge(getInitialState(), initialValues),
  );

  const mutations = useMemo<Mutations>(
    () => ({
      editorMounted: (quill) => {
        dispatch({ type: 'editorMounted', value: quill });
      },
      editorUnmounted: () => {
        dispatch({ type: 'editorUnmounted' });
      },
      localSelection: ({
        range,
        formats,
      }: {
        range: Range | null;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formats: Record<string, any>;
      }) => {
        dispatch({ type: 'localSelection', value: { range, formats } });
      },
      editingLinkChange: (editing: boolean) => {
        dispatch({ type: 'editingLinkChange', value: editing });
      },
      readOnlyChange: (readOnly: boolean) => {
        dispatch({ type: 'readOnlyChange', value: readOnly });
      },
      onActiveBlotChange: (blot: Blot | null) => {
        dispatch({ type: 'activeBlotChange', value: blot });
      },
    }),
    [dispatch],
  );

  return (
    <Context.Consumer>
      {(parentValue) => (
        <Context.Provider
          // eslint-disable-next-line react/jsx-no-constructed-context-values -- Fix next time editing the file
          value={{
            ...state,
            ...mutations,
            ancestorQuill: parentValue ? parentValue.quill : state.quill,
          }}
        >
          {children}
        </Context.Provider>
      )}
    </Context.Consumer>
  );
};

export const withQuillContextProvider = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  const WithQuillContextProvider = (props: P) => (
    <QuillContext>
      <Component {...props} />
    </QuillContext>
  );
  return HOCWithDisplayName('withQuillContextProvider')(
    WithQuillContextProvider,
  );
};

export default QuillContext;
